import { EngagementStepStatus } from '@aiwyn/api';
import { SecureLinkEntityType } from '@aiwyn/auth';
import {
  DestinationEngagementStepRef,
  EngagementHubContainerComponent,
  EngagementHubView,
  EngagementTrackingService,
  EngagementsState,
  GetEngagementHub,
  MoveToEngagementStep,
  SetSelectedEngagementId
} from '@aiwyn/engagements';
import { Logger } from '@aiwyn/shared';

import { Component, computed, effect, inject, input, output, untracked } from '@angular/core';
import { Store } from '@ngxs/store';
import { defer, of, switchMap } from 'rxjs';
import {
  OnSecureLinkEntityStatusUpdatedFn,
  SecureLinkDisplayAdaptor,
  SecureLinkEngagementStep,
  SecureLinkEntityIdentity,
  SecureLinkEntityStatus,
} from '../../models';

@Component({
  selector: 'app-engagement-wrapper',
  templateUrl: './engagement-wrapper.component.html',
  styleUrl: './engagement-wrapper.component.scss',
  standalone: true,
  imports: [EngagementHubContainerComponent],
})
export class EngagementWrapperComponent extends SecureLinkDisplayAdaptor<SecureLinkEntityType.ENGAGEMENT_STEP> {
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);
  private readonly engagementTrackingService = inject(EngagementTrackingService);

  readonly entity = input<SecureLinkEngagementStep>();
  readonly initialView = input<EngagementHubView>();
  readonly onGoToStep = input<(destination?: DestinationEngagementStepRef) => void>();
  readonly onNavigateToNextEntity = input<() => void>();
  readonly onNavigateToPreviousEntity = input<() => void>();
  readonly onStatusUpdated = input<OnSecureLinkEntityStatusUpdatedFn>();
  readonly showSideDrawer = input<boolean>();

  readonly engagementHubView = output<EngagementHubView>();
  readonly sideDrawerOpened = output<boolean>();

  // Track the last computed status per entity to prevent false status updates
  private lastComputedStatus = new Map<SecureLinkEntityIdentity, EngagementStepStatus>();

  selectedEngagement = this.store.selectSignal(EngagementsState.getSelectedEngagement);
  status = computed<EngagementStepStatus>(() => {
    const step = this.entity();
    if (!step) {
      return;
    }

    const report = this.selectedEngagement()?.report;
    if (!report) {
      return;
    }

    const entityDocumentId = step.documentId ?? step.tasks?.find(task => task.documentId)?.documentId;
    
    const matchingStep = report.steps.find(reportStep => {
      if (reportStep.type !== step.type) {
        return false;
      }

      const reportDocumentId = reportStep.documentId ?? reportStep.tasks?.find(task => task.documentId)?.documentId;

      if (entityDocumentId != null || reportDocumentId != null) {
        return entityDocumentId === reportDocumentId;
      }

      return true;
    });

    return matchingStep?.status;
  });

  constructor() {
    super();

    /**
     * When the entity (engagement step) changes, ensure the corresponding engagement is selected in the store,
     * and navigate to the matching step.
     */
    effect(() => {
      const engagementStep = this.entity();
      if (!engagementStep) {
        return;
      }

      this.engagementTrackingService.trackEngagementOpened(engagementStep.engagementId);

      // check if selected step's engagement id is different from current engagement id
      const setNewSelectedEngagement$ = engagementStep.engagementId !== this.store.selectSnapshot(EngagementsState.getSelectedEngagementId)
        ? defer(() => this.store.dispatch(new SetSelectedEngagementId(engagementStep.engagementId))).pipe(
          switchMap(_ => {
            const selectedEngagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);
            return this.store.dispatch(new GetEngagementHub(selectedEngagementId));
          }),
        )
        : of(undefined);

      setNewSelectedEngagement$.pipe(
        switchMap(_ => {
          const currentStepRef = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef);
          
          // No current step ref means this is the first time loading this engagement in this session, navigate to the first incomplete step
          if (currentStepRef == null) {
            return this.store.dispatch(new MoveToEngagementStep(undefined));
          }
          
          // Otherwise, navigate to the specific step from the entity
          const stepDocumentId = engagementStep.documentId ?? engagementStep.tasks?.find(task => task.documentId)?.documentId;
          
          const destination = stepDocumentId != null
            ? { type: engagementStep.type, documentId: stepDocumentId }
            : engagementStep.type;

          return this.store.dispatch(new MoveToEngagementStep(destination));
        }),
      ).subscribe();
    });

    /**
     * When the status of the engagement step changes, invoke the onStatusUpdated callback if provided.
     * Only call the callback if the normalized status has actually changed from what was last reported.
     * This prevents duplicate status updates when navigating to an already-completed step.
     */
    effect(() => {
      const entity = untracked(() => this.entity());
      const status = this.status();
      if (!entity || !status) {
        return;
      }

      const entityIdentity = EngagementWrapperComponent.getEntityIdentity(entity);
      const lastStatus = this.lastComputedStatus.get(entityIdentity);
      const normalizedStatus = EngagementWrapperComponent.normalizeStatus(status);
      const lastNormalizedStatus = lastStatus ? EngagementWrapperComponent.normalizeStatus(lastStatus) : undefined;
      
      // Only proceed if the normalized status has actually changed
      // This prevents calling onStatusUpdated when navigating to an already-completed step
      // However, we always report the status at least once (when lastStatus is undefined)
      if (lastStatus !== undefined && lastNormalizedStatus === normalizedStatus) {
        return;
      }

      // Update the tracked status
      this.lastComputedStatus.set(entityIdentity, status);

      const onStatusUpdated = untracked(() => this.onStatusUpdated());
      if (!onStatusUpdated) {
        this.logger.warn('EngagementWrapperComponent: onStatusUpdated callback is not provided at time of status update.');
        return;
      }

      onStatusUpdated(entityIdentity, normalizedStatus);
    });
  }

  static normalizeStatus(status: EngagementStepStatus): SecureLinkEntityStatus {
    return status === EngagementStepStatus.COMPLETE
      ? SecureLinkEntityStatus.COMPLETED
      : SecureLinkEntityStatus.IN_PROGRESS;
  }

  static getEntityIdentity(engagementStep: SecureLinkEngagementStep): SecureLinkEntityIdentity<SecureLinkEntityType.ENGAGEMENT_STEP> {
    const identifier = engagementStep.documentContactId ?? engagementStep.engagementId;
    return `${SecureLinkEntityType.ENGAGEMENT_STEP}-${identifier}-${engagementStep.type}`;
  }
}
